import { inject, Injectable } from '@angular/core';
import { arrayAdd, arrayRemove, arrayUpdate } from '@datorama/akita';
import { combineLatest, firstValueFrom, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import {
  ChangeLogItemStatus,
  GqlService,
  RequesterType,
  updateChangeLogItemMutation,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { Utils } from '@shared/utils/utils';

import { ChangeLogItemModel, ChangeLogModel, ChangeLogStore } from './change-log.store';

@Injectable({ providedIn: 'root' })
export class ChangeLogService {
  private changeLogStore = inject(ChangeLogStore);
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private vendorsQuery = inject(OrganizationQuery);

  add(changeLog: ChangeLogModel) {
    this.changeLogStore.add(changeLog);
  }

  async addLogItem({
    cause,
    change_log_id,
    change_log_item_status,
    change_order_reference,
    cnf_no,
    description,
    investigator_fee,
    is_planned,
    organization_id,
    passthrough_fee,
    request_date,
    requester,
    service_fee,
    start_date,
  }: {
    cause: null | string;
    change_log_id: string;
    change_log_item_status: ChangeLogItemStatus;
    change_order_reference: string;
    cnf_no: number;
    description: string;
    investigator_fee: null | number;
    is_planned: boolean;
    organization_id: string;
    passthrough_fee: null | number;
    request_date: string;
    requester: RequesterType;
    service_fee: null | number;
    start_date: string;
  }) {
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.createChangeLogItem$({
        cause,
        change_log_id,
        change_log_item_status,
        change_order_reference,
        cnf_no,
        description,
        investigator_fee,
        is_planned,
        organization_id,
        passthrough_fee,
        request_date,
        requester,
        service_fee,
        start_date,
      })
    );
    let parsedData: ChangeLogItemModel | null = null;

    if (success && data) {
      parsedData = this.parseLogItems(data);
      this.changeLogStore.update(change_log_id, ({ change_log_items }) => {
        const newArr = arrayAdd(change_log_items, parsedData);
        const totals = this.getChangeLogStoreTotals(change_log_id, newArr);

        return {
          ...totals,
        };
      });
    } else {
      this.overlayService.error(errors);
    }

    return { data: parsedData, errors, success };
  }

  dateFormatter(dateStr: null | string | undefined) {
    try {
      return Intl.DateTimeFormat('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }).format(Utils.dateParse(dateStr));
    } catch {
      return Utils.zeroHyphen;
    }
  }

  async deleteLogItem(id: string, change_log_id: string) {
    this.changeLogStore.setLoading(true);

    const { data, errors, success } = await firstValueFrom(
      this.gqlService.removeChangeLogItem$(id)
    );

    if (success && data) {
      this.changeLogStore.update(change_log_id, ({ change_log_items }) => {
        const newArr = arrayRemove(change_log_items, id);
        const totals = this.getChangeLogStoreTotals(change_log_id, newArr);

        return {
          ...totals,
        };
      });
      this.overlayService.success('Successfully removed!');
    } else {
      this.overlayService.error(errors);
    }

    this.changeLogStore.setLoading(false);
  }

  get() {
    return combineLatest([
      this.mainQuery.select('trialKey'),
      this.vendorsQuery.selectActive((x) => x.id),
    ]).pipe(
      switchMap(([trial_id, vendor_id]) => {
        this.changeLogStore.setLoading(true);

        if (trial_id && vendor_id) {
          return this.gqlService.listChangeLogs$(vendor_id);
        }

        return of({ data: [], errors: [], success: false });
      }),
      map(({ data: rawData, errors, success }) => {
        let data: ChangeLogModel[] = [];

        if (success && rawData) {
          data = rawData.map((x) => ({
            ...x,
            change_log_items: [] as ChangeLogItemModel[],
            clis_total: '$0',
            investigator_fee_percent: 0,
            investigator_fees_total: '$0',
            pass_thru_percent: 0,
            pass_thru_total: '$0',
            services_percent: 0,
            services_total: '$0',
          }));
        }

        this.changeLogStore.set(data);

        data.forEach(({ change_log_status, id }) => {
          if (change_log_status === 'STATUS_OPEN') {
            this.changeLogStore.setActive(id);
          }
        });

        this.changeLogStore.setLoading(false);

        return { data, errors, success };
      })
    );
  }

  getChangeLogStoreTotals(change_log_id: string, change_log_items: ChangeLogItemModel[]) {
    let clis_total = 0;
    let services_total = 0;
    let investigator_fees_total = 0;
    let pass_thru_total = 0;

    change_log_items.forEach((item) => {
      clis_total += item.service_amount + item.passthrough_amount + item.investigator_amount;
      services_total += item.service_amount;
      investigator_fees_total += item.investigator_amount;
      pass_thru_total += item.passthrough_amount;
    });

    return {
      change_log_items,
      clis_total: Utils.currencyFormatter(clis_total),
      investigator_fee_percent: Math.round((investigator_fees_total / clis_total) * 100),
      investigator_fees_total: Utils.currencyFormatter(investigator_fees_total),
      pass_thru_percent: Math.round((pass_thru_total / clis_total) * 100),
      pass_thru_total: Utils.currencyFormatter(pass_thru_total),
      services_percent: Math.round((services_total / clis_total) * 100),
      services_total: Utils.currencyFormatter(services_total),
    };
  }

  getFilePath(changeLogId: string, cnf_no: number) {
    const trialId = this.mainQuery.getValue().trialKey;
    const vendorId = this.vendorsQuery.getActiveId() || '';

    return `trials/${trialId}/vendors/${vendorId}/changelogs/${changeLogId}/changelogitems/${cnf_no}`;
  }

  getLogItems(change_log_id: string) {
    this.changeLogStore.setLoading(true);

    return this.gqlService.listChangeLogItems$(change_log_id).pipe(
      map(({ data, errors, success }) => {
        if (success && data) {
          const parsed_change_log_items = data.map((item) => {
            return {
              ...this.parseLogItems(item),
              file_link: this.getFilePath(change_log_id, item.cnf_no),
            };
          });

          this.changeLogStore.update(change_log_id, {
            ...this.getChangeLogStoreTotals(change_log_id, parsed_change_log_items),
          });
        }

        this.changeLogStore.setLoading(false);

        return { data, errors, success };
      })
    );
  }

  parseLogItems(item: updateChangeLogItemMutation) {
    const amounts = {
      AMOUNT_INVESTIGATOR: 0,
      AMOUNT_PASSTHROUGH: 0,
      AMOUNT_SERVICE: 0,
      AMOUNT_TOTAL: 0,
    } as { [k: string]: number };

    for (const amountObj of item.expense_amounts || []) {
      if (amountObj) {
        amounts[amountObj.amount_type] = amountObj.amount || 0;
      }
    }

    return {
      ...item,

      investigator_amount: amounts.AMOUNT_INVESTIGATOR || 0,
      passthrough_amount: amounts.AMOUNT_PASSTHROUGH || 0,
      req_start_date:
        item.request_date && item.start_date
          ? `${this.dateFormatter(item.request_date)} / ${this.dateFormatter(item.start_date)}`
          : '',
      service_amount: amounts.AMOUNT_SERVICE || 0,
      total_amount: amounts.AMOUNT_TOTAL || 0,
    } as ChangeLogItemModel;
  }

  remove(id: string) {
    this.changeLogStore.remove(id);
  }

  update(id: string, changeLog: Partial<ChangeLogModel>) {
    this.changeLogStore.update(id, changeLog);
  }

  async updateLogItem({
    cause,
    change_log_id,
    change_log_item_id,
    change_log_item_status,
    change_order_reference,
    cnf_no,
    description,
    investigator_fee,
    is_planned,
    organization_id,
    passthrough_fee,
    request_date,
    requester,
    service_fee,
    start_date,
  }: {
    cause: string;
    change_log_id: string;
    change_log_item_id: string;
    change_log_item_status: ChangeLogItemStatus;
    change_order_reference: string;
    cnf_no: number;
    description: string;
    investigator_fee: number;
    is_planned: boolean;
    organization_id: string;
    passthrough_fee: number;
    request_date: string;
    requester: RequesterType;
    service_fee: number;
    start_date: string;
  }) {
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.updateChangeLogItem$({
        cause,
        change_log_id,
        change_log_item_id,
        change_log_item_status,
        change_order_reference,
        cnf_no,
        description,
        investigator_fee,
        is_planned,
        organization_id,
        passthrough_fee,
        request_date,
        requester,
        service_fee,
        start_date,
      })
    );
    let parsedData: ChangeLogItemModel | null = null;

    if (success && data) {
      this.changeLogStore.update(change_log_id, ({ change_log_items }) => {
        parsedData = this.parseLogItems(data);
        const new_log_items = arrayUpdate(change_log_items, change_log_item_id, parsedData);
        const updatedTotals = this.getChangeLogStoreTotals(change_log_id, new_log_items);

        return {
          ...updatedTotals,
        };
      });
    } else {
      this.overlayService.error(errors);
    }

    return { data: parsedData, errors, success };
  }
}
